import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";

import NavigationTabBar from "../../components/navigation/NavigationTabBar";
import FooterSection from "../../components/navigation/FooterSection";
import { useAuth } from "../../components/navigation/AuthenticationStateManager";
import Icon from "../../components/AppIcon";

const BootcampHub = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [bootcamps, setBootcamps] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [timeNow, setTimeNow] = useState(new Date());

  // fetch
  useEffect(() => {
    const loadBootcamps = async () => {
      try {
        const res = await api.get("/api/bootcamp/bootcamps");
        setBootcamps(res.data);
      } catch {
        console.error("Failed to load bootcamps");
      }
    };
    loadBootcamps();
  }, []);

  // timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // -------- HELPERS --------
  const isExpired = (date) => new Date(date) < timeNow;

  const isLive = (date) => {
    const diff = new Date(date) - timeNow;
    return diff <= 0 && diff > -2 * 60 * 60 * 1000;
  };

  const getCountdown = (date) => {
    const diff = new Date(date) - timeNow;

    if (diff <= 0) return "In Progress";

    const hrs = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff / (1000 * 60)) % 60);

    return `${hrs}h ${mins}m left`;
  };

  // -------- FILTER --------
  const categories = [
    "all",
    ...new Set(bootcamps.map((b) => b.category).filter(Boolean)),
  ];

  const filteredBootcamps =
    selectedCategory === "all"
      ? bootcamps
      : bootcamps.filter((b) => b.category === selectedCategory);

  // -------- SPLIT DATA --------
  const upcomingBootcamps = filteredBootcamps
    .filter((b) => new Date(b.date) > timeNow)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const completedBootcamps = filteredBootcamps
    .filter((b) => new Date(b.date) <= timeNow)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // -------- FEATURED --------
  const featuredBootcamp =
    upcomingBootcamps[0] || completedBootcamps[0];

  const featuredExpired =
    featuredBootcamp && isExpired(featuredBootcamp.date);

  const featuredLive =
    featuredBootcamp && isLive(featuredBootcamp.date);

  return (
    <div className="min-h-screen bg-background">
      <NavigationTabBar isAuthenticated={isAuthenticated} />

      <main className="pt-20 pb-20">
        <div className="max-w-[1400px] mx-auto px-6">

          {/* HERO */}
          <div className="text-center mb-20">
            <h1 className="text-5xl font-bold mb-6">
              Live Tech Bootcamps
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Accelerate your career with short instructor-led bootcamps
              in Python, SQL and Data Engineering.
            </p>
          </div>

          {/* FEATURED */}
          {featuredBootcamp && (
            <div
              className={`rounded-2xl p-10 mb-16 text-center transition
              ${
                featuredExpired
                  ? "bg-gray-100 text-gray-700"
                  : "bg-gradient-to-r from-indigo-600 to-blue-500 text-white"
              }`}
            >
              <h2 className="text-3xl font-bold mb-2">
                {featuredExpired
                  ? "Bootcamp Completed"
                  : featuredLive
                  ? "🔴 Live Now"
                  : "Upcoming Bootcamp"}
              </h2>

              <p className="text-lg mb-2">
                {featuredBootcamp.title}
              </p>

              <p className="text-sm mb-4">
                {featuredExpired
                  ? "This session has already been completed"
                  : `⏳ ${getCountdown(featuredBootcamp.date)}`}
              </p>

              <button
                disabled={featuredExpired}
                onClick={() =>
                  !featuredExpired &&
                  navigate(`/bootcamp/register/${featuredBootcamp._id}`)
                }
                className={`px-6 py-3 rounded-lg transition
                  ${
                    featuredExpired
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-white text-primary hover:scale-105"
                  }`}
              >
                {featuredExpired
                  ? "Session Completed"
                  : featuredLive
                  ? "Join Now"
                  : "Register Now"}
              </button>
            </div>
          )}

          {/* CATEGORY */}
          <div className="flex justify-center gap-4 mb-14 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2 rounded-full border transition
                  ${
                    selectedCategory === cat
                      ? "bg-primary text-white"
                      : "hover:bg-gray-100"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* UPCOMING */}
          {upcomingBootcamps.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-6">
                Upcoming Bootcamps
              </h2>

              <div className="grid md:grid-cols-3 gap-10">
                {upcomingBootcamps.map((b) => {
                  const live = isLive(b.date);

                  const totalSeats = b.totalSeats || 100;
                  const filled = totalSeats - b.seatsLeft;
                  const percent = (filled / totalSeats) * 100;

                  return (
                    <div
                      key={b._id}
                      className="bg-card border rounded-2xl p-8 shadow hover:shadow-xl transition"
                    >
                      <div className="flex justify-between">
                        <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                          {b.category}
                        </span>

                        {percent > 70 && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                            🔥 Trending
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold mt-4 mb-2">
                        {b.title}
                      </h3>

                      <p className="text-sm mb-1">
                        Trainer: {b.trainer}
                      </p>

                      <p className="text-sm mb-2">
                        ⏳ {getCountdown(b.date)}
                      </p>

                      {/* progress */}
                      <div className="mb-3">
                        <div className="w-full bg-gray-200 h-2 rounded-full">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="text-xs mt-1">
                          {filled}/{totalSeats} filled
                        </p>
                      </div>

                      <button
                        onClick={() =>
                          navigate(`/bootcamp/register/${b._id}`)
                        }
                        className="w-full bg-primary text-white py-2 rounded-lg"
                      >
                        {live ? "Join Now" : "Register Now"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* COMPLETED */}
          {completedBootcamps.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mt-16 mb-6 text-gray-500">
                Completed Sessions
              </h2>

              <div className="grid md:grid-cols-3 gap-10 opacity-70">
                {completedBootcamps.map((b) => (
                  <div
                    key={b._id}
                    className="bg-card border rounded-2xl p-8"
                  >
                    <h3 className="text-xl font-bold mb-2">
                      {b.title}
                    </h3>

                    <p className="text-sm mb-2">
                      Trainer: {b.trainer}
                    </p>

                    <p className="text-sm mb-3">
                      Session Completed
                    </p>

                    <button
                      disabled
                      className="w-full bg-gray-300 py-2 rounded-lg"
                    >
                      Completed
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* EMPTY STATE */}
          {upcomingBootcamps.length === 0 &&
            completedBootcamps.length === 0 && (
              <div className="text-center py-20">
                <h3 className="text-xl font-semibold mb-2">
                  No Bootcamps Available
                </h3>
                <p className="text-muted-foreground">
                  New sessions coming soon 🚀
                </p>
              </div>
            )}

        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default BootcampHub;